import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { DragEndEvent } from '@dnd-kit/core';
import { updateTask } from './useTasks';
import { addProgressNote } from './useProgressNotes';
import { useAppStore } from '../store';
import { useToast } from '../components/Toast/ToastProvider';
import type { Task } from '../types';

export interface DragResult {
  taskId: string;
  taskTitle: string;
  progressBefore: number;
  progressAfter: number;
}

export function useTrackDrag(
  trackRef: React.RefObject<HTMLDivElement | null>,
  onDragComplete?: (result: DragResult) => void,
  dragStartRowRect?: React.RefObject<DOMRect | null>,
  nearDoneRef?: React.RefObject<boolean>
) {
  const queryClient = useQueryClient();
  const spaceId = useAppStore((s) => s.currentSpaceId);
  const { addToast } = useToast();

  const progressMutation = useMutation({
    mutationFn: ({ taskId, newProgress, isCompleted }: { taskId: string; newProgress: number; isCompleted: boolean; parentId?: string | null }) =>
      updateTask(taskId, {
        trackProgress: newProgress,
        ...(isCompleted ? { status: 'done', completedAt: new Date().toISOString() } : {}),
      }),
    onMutate: async ({ taskId, newProgress, isCompleted }) => {
      // Cancel any in-flight refetches so they don't overwrite optimistic update
      await queryClient.cancelQueries({ queryKey: ['tasks', spaceId, 'track'] });
      // Snapshot previous value for rollback
      const previous = queryClient.getQueryData<Task[]>(['tasks', spaceId, 'track']);
      // Optimistically update cache
      queryClient.setQueryData<Task[]>(['tasks', spaceId, 'track'], (old) =>
        old?.map((t) =>
          t.id === taskId
            ? { ...t, trackProgress: newProgress, ...(isCompleted ? { status: 'done' as const } : {}) }
            : t
        )
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      // Rollback to previous state
      if (context?.previous) {
        queryClient.setQueryData(['tasks', spaceId, 'track'], context.previous);
      }
      addToast("Couldn't save progress, changes reverted", 'error');
    },
    onSettled: (_data, _err, vars) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', spaceId, 'track'] });
      if (vars.parentId) {
        queryClient.invalidateQueries({ queryKey: ['subtasks', vars.parentId] });
        queryClient.invalidateQueries({ queryKey: ['subtasks', 'done-count', vars.parentId] });
      }
    },
  });

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, delta, activatorEvent } = event;
      const taskId = active.id as string;
      const data = active.data.current as { trackProgress: number; title: string; parentId?: string | null; canComplete?: boolean } | undefined;
      const currentProgress = data?.trackProgress ?? 0;
      const title = data?.title ?? '';
      const parentId = data?.parentId;

      const track = trackRef.current;
      if (!track) return;

      // No-op if pointer ended far outside the starting row (skip when near-done zone is active)
      if (!nearDoneRef?.current && dragStartRowRect?.current && activatorEvent) {
        const mouseEvent = activatorEvent as MouseEvent;
        const pointerY = mouseEvent.clientY + delta.y;
        const rowRect = dragStartRowRect.current;
        const padding = rowRect.height * 0.5;
        if (pointerY < rowRect.top - padding || pointerY > rowRect.bottom + padding) return;
      }

      const rect = track.getBoundingClientRect();

      let newProgress: number;
      if (nearDoneRef?.current) {
        newProgress = 100;
      } else {
        const deltaPercent = (delta.x / rect.width) * 100;
        newProgress = Math.round(currentProgress + deltaPercent);
        newProgress = Math.max(0, Math.min(100, newProgress));
        if (newProgress >= 95) newProgress = 100;
      }
      if (newProgress === currentProgress) return;

      const isCompleted = newProgress === 100;
      if (isCompleted && data?.canComplete === false) {
        addToast('Complete subtasks first', 'error');
        return;
      }

      // Fire optimistic mutation (non-blocking)
      progressMutation.mutate({ taskId, newProgress, isCompleted, parentId });

      // Secondary actions fire immediately regardless of mutation result
      addProgressNote(taskId, '', currentProgress, newProgress);
      window.dispatchEvent(new CustomEvent('tutorial:drag-complete', { detail: { progress: newProgress } }));
      if (onDragComplete) {
        onDragComplete({ taskId, taskTitle: title, progressBefore: currentProgress, progressAfter: newProgress });
      }
    },
    [trackRef, onDragComplete, dragStartRowRect, nearDoneRef, progressMutation]
  );

  return { handleDragEnd };
}
